
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import OsSheetContent from '@/components/core/sheet/OsSheetContent.vue';
import OsSheet from '@/components/core/sheet/OsSheet.vue';
import OsAntesDiscEditor from '@/components/chart/OsAntesDiscEditor.vue';
import OsParticipantOverview from '@/components/shared/participants/OsParticipantOverview.vue';
import OsAntesDiscEditorValidator from '@/components/chart/OsAntesDiscEditorValidator.vue';
import { IParticipant, ITargetProfile } from '@/models/application';
import { customersApi, reportsApi } from '@/apis';
import userModule from '@/store/UserModule';
import { IReportContent } from '@/models/application/IReportContent';
import OsButton from '@/components/core/OsButton.vue';
import OsEntityToolbar from '@/components/core/OsEntityToolbar.vue';
import OsInputTextField from '@/components/core/input/OsInputTextField.vue';
import { AppDirtyUtil } from '@/utils/AppDirtyUtil';
import { StringValidationUtil } from '@/utils/StringValidationUtil';
import OsTargetProfilePdfDownloadBtn from '@/components/shared/target-profiles/OsTargetProfilePdfDownloadBtn.vue';
import OsEntityForm from '@/components/core/OsEntityForm.vue';
import { EntityFormFieldType, IEntityFormField } from '@/models/core';
@Component({
  components: {
    OsEntityForm,
    OsTargetProfilePdfDownloadBtn,
    OsInputTextField,
    OsEntityToolbar,
    OsButton,
    OsParticipantOverview,
    OsAntesDiscEditor,
    OsAntesDiscEditorValidator,
    OsSheet,
    OsSheetContent
  }
})
export default class OsTargetProfileForm extends Vue {
  @Prop({ default: 'Target profile' })
  title!: string;

  @Prop({ required: false, default: '' })
  campaignId!: string;

  @Prop({ required: false, default: false })
  showCampaignLookup!: boolean;

  appDirtyUtil: AppDirtyUtil = new AppDirtyUtil();
  targetProfileId = '';
  targetProfile: ITargetProfile | null = null;
  downloading = false;
  reportContent: IReportContent | null = null;
  participants: IParticipant[] = [];
  profile = '';
  loading = false;
  dragging = false;
  discIsValid = false;

  customerId!: string;
  internalCampaignId!: string;

  mainFieldsAreValid = false;
  get mainFields(): IEntityFormField[] {
    let fields: IEntityFormField[] = [
      {
        type: EntityFormFieldType.text,
        label: this.$tc('form.target_profile.name'),
        propName: 'name',
        smCols: this.showCampaignLookup ? 6 : 12,
        required: true
      }
    ];

    if (this.showCampaignLookup) {
      fields.push({
        type: EntityFormFieldType.lookup,
        label: this.$tc('form.target_profile.campaign'),
        propName: 'campaignId',
        smCols: 6,
        required:
          !this.$global.roles.isAdmin() && !this.$global.roles.isTrial(),
        entityName: 'campaign'
      });
    }

    return fields;
  }

  get isDirty() {
    return AppDirtyUtil.isDirty;
  }

  get targetReportText(): string {
    if (this.reportContent === null) {
      return '';
    }

    let text = this.reportContent.content;

    if (this.targetProfile?.name != null) {
      text = text.replace(new RegExp('%Stelle%', 'g'), this.targetProfile.name);
    }

    return text;
  }

  async created() {
    this.customerId = userModule.state.customer!.id;
    this.targetProfileId = this.$route.params.targetprofileid;

    if (StringValidationUtil.isNullOrWhitespace(this.campaignId)) {
      this.internalCampaignId = this.$route.params.campaignid;
    } else {
      this.internalCampaignId = this.campaignId;
    }

    this.targetProfile = await customersApi.getTargetProfileById(
      this.customerId,
      this.targetProfileId
    );

    this.appDirtyUtil.init(this.targetProfile);
  }

  async undoClicked() {
    this.targetProfile = this.appDirtyUtil.getOriginalObject();
    this.appDirtyUtil.init(this.targetProfile);
    await this.executeRequests();
  }

  async saveClicked() {
    if (!this.mainFieldsAreValid) {
      return;
    }

    this.targetProfile = await customersApi.updateTargetProfile(
      this.customerId,
      this.targetProfile!
    );
    this.appDirtyUtil.init(this.targetProfile);
  }

  async deleteClicked() {
    if (
      await this.$global.dialogs.confirm(
        this.$tc('form.target_profile.delete_dialog_header'),
        this.$t('form.target_profile.delete_dialog_text', {
          name: this.targetProfile?.name
        }) as string
      )
    ) {
      if (
        await customersApi.deleteTargetProfile(
          this.customerId,
          this.targetProfileId
        )
      ) {
        this.$emit('recordDeleted');
      }
    }
  }

  @Watch('targetProfile', { deep: true })
  onTargetProfileChanged() {
    this.appDirtyUtil.update(this.targetProfile);
  }

  @Watch('targetProfile.campaignId', { deep: false })
  onTargetProfileChanged2(
    newValue: string | undefined,
    oldValue: string | undefined
  ) {
    if (oldValue && newValue != oldValue) {
      this.executeRequests();
    }
  }

  async renderPdf() {
    if (this.targetProfile === null) {
      return;
    }

    this.downloading = true;
    let filename = `Target_Profile_${this.profile}.pdf`;

    if (this.profile) {
      await reportsApi.renderTargetProfile(
        filename,
        this.profile,
        this.targetProfile.name,
        this.$i18n.locale,
        this.targetProfile
      );
    }
    this.downloading = false;
  }

  @Watch('discIsValid')
  async executeRequests() {
    if (!this.discIsValid) {
      this.reportContent = null;
      this.participants = [];
    }

    if (
      this.targetProfile === null ||
      this.targetProfile.dValue == null ||
      this.targetProfile.iValue == null ||
      this.targetProfile.sValue == null ||
      this.targetProfile.cValue == null
    ) {
      return;
    }

    if (!this.dragging && this.discIsValid) {
      this.loading = true;
      this.participants = [];

      if (this.targetProfile.campaignId) {
        this.participants = await customersApi.getForCampaignByDISCValuesAsync(
          this.customerId,
          this.targetProfile.campaignId,
          this.targetProfile.dValue,
          this.targetProfile.iValue,
          this.targetProfile.sValue,
          this.targetProfile.cValue
        );
      }

      if (this.profile) {
        this.reportContent = await reportsApi.getTargetProfileContent(
          this.profile,
          this.$i18n.locale
        );
      }

      this.loading = false;
    } else {
      this.participants = [];
    }
  }
}
